(() => {
  'use strict';

  const config = window.VIP_PARENT_CLUB_CONFIG;

  if (!config) {
    return;
  }

  if (window.vipParentClubReliableIntegration) {
    return;
  }

  window.vipParentClubReliableIntegration = true;

  const MAIN_PRODUCT_ID = Number(config.mainProductId || 0);
  const VIP_PRODUCT_ID = Number(config.vipProductId || 0);
  const VIP_VARIANT_ID = Number(config.vipVariantId || 0);
  const VIP_SELLING_PLAN_ID = Number(config.vipSellingPlanId || 0);
  const VIP_IMAGE = config.vipImage || '';
  const VIP_TITLE = config.vipTitle || 'VIP Parent Club';
  const SHOPIFY_ROOT = window.Shopify?.routes?.root || '/';

  if (
    !MAIN_PRODUCT_ID ||
    !VIP_PRODUCT_ID ||
    !VIP_VARIANT_ID ||
    !VIP_SELLING_PLAN_ID
  ) {
    console.warn('VIP Parent Club configuration is incomplete.', config);
    return;
  }

  const originalFetch = window.fetch.bind(window);
  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  let syncRunning = false;
  let syncRequestedAgain = false;
  let syncTimer = null;
  let pollTimer = null;
  let removalTimer = null;
  let checkoutRunning = false;
  let mutationDebounce = null;
  let reloadStarted = false;

  async function getCart() {
    const response = await originalFetch(
      `${SHOPIFY_ROOT}cart.js?_=${Date.now()}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error('Unable to read Shopify cart.');
    }

    return response.json();
  }

  function getMainLines(cart) {
    return cart.items.filter((item) => {
      return Number(item.product_id) === MAIN_PRODUCT_ID;
    });
  }

  function getMainQuantity(cart) {
    return getMainLines(cart).reduce((total, item) => {
      return total + Number(item.quantity || 0);
    }, 0);
  }

  function getAllVipLines(cart) {
    return cart.items.filter((item) => {
      return (
        Number(item.product_id) === VIP_PRODUCT_ID ||
        Number(item.variant_id) === VIP_VARIANT_ID
      );
    });
  }

  function getVipSubscriptionLines(cart) {
    return getAllVipLines(cart).filter((item) => {
      return Boolean(item.selling_plan_allocation);
    });
  }

  function getVipNormalLines(cart) {
    return getAllVipLines(cart).filter((item) => {
      return !item.selling_plan_allocation;
    });
  }

  async function changeLine(key, quantity) {
    const response = await originalFetch(
      `${SHOPIFY_ROOT}cart/change.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result.description ||
        result.message ||
        'Unable to update cart.'
      );
    }

    return result;
  }

  async function addVipSubscription() {
    const response = await originalFetch(
      `${SHOPIFY_ROOT}cart/add.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: VIP_VARIANT_ID,
              quantity: 1,
              selling_plan: VIP_SELLING_PLAN_ID,
              properties: {
                '_VIP offer': 'First month free',
                '_Added with': 'Bible Study Workbook bundle'
              }
            }
          ]
        })
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result.description ||
        result.message ||
        'Unable to add VIP Parent Club.'
      );
    }

    return result;
  }

  async function removeAllVip(cart) {
    const vipLines = getAllVipLines(cart);

    for (const line of vipLines) {
      await changeLine(line.key, 0);
    }
  }

  async function normalizeVip(cart) {
    let changed = false;

    const normalLines = getVipNormalLines(cart);

    for (const line of normalLines) {
      await changeLine(line.key, 0);
      changed = true;
    }

    if (normalLines.length) {
      cart = await getCart();
    }

    let subscriptionLines = getVipSubscriptionLines(cart);

    if (!subscriptionLines.length) {
      await addVipSubscription();
      changed = true;

      cart = await getCart();
      subscriptionLines = getVipSubscriptionLines(cart);
    }

    if (subscriptionLines.length) {
      const firstLine = subscriptionLines[0];

      if (Number(firstLine.quantity) !== 1) {
        await changeLine(firstLine.key, 1);
        changed = true;
      }

      for (const duplicate of subscriptionLines.slice(1)) {
        await changeLine(duplicate.key, 0);
        changed = true;
      }
    }

    return changed;
  }

  async function syncVipState() {
    if (syncRunning) {
      syncRequestedAgain = true;
      return;
    }

    syncRunning = true;
    syncRequestedAgain = false;

    try {
      let cart = await getCart();

      const mainQuantity = getMainQuantity(cart);
      const vipLines = getAllVipLines(cart);

      if (mainQuantity <= 0) {
        if (vipLines.length) {
          await removeAllVip(cart);
        }

        return;
      }

      await normalizeVip(cart);

      cart = await getCart();

      const subscriptionLines = getVipSubscriptionLines(cart);

      if (!subscriptionLines.length) {
        throw new Error('VIP subscription was not added.');
      }

      if (subscriptionLines.length > 1) {
        for (const duplicate of subscriptionLines.slice(1)) {
          await changeLine(duplicate.key, 0);
        }
      }

      if (Number(subscriptionLines[0].quantity) !== 1) {
        await changeLine(subscriptionLines[0].key, 1);
      }

      hideVipRemoveButton();
    } catch (error) {
      console.error('VIP Parent Club synchronization failed:', error);
    } finally {
      syncRunning = false;

      if (syncRequestedAgain) {
        syncRequestedAgain = false;

        window.setTimeout(() => {
          syncVipState();
        }, 40);
      }
    }
  }

  function scheduleSync(delay = 100) {
    window.clearTimeout(syncTimer);

    syncTimer = window.setTimeout(() => {
      syncVipState();
    }, delay);
  }

  async function verifyVipBeforeCheckout() {
    if (checkoutRunning) {
      return false;
    }

    checkoutRunning = true;

    try {
      await syncVipState();

      let cart = await getCart();

      if (getMainQuantity(cart) <= 0) {
        return true;
      }

      let vipLines = getVipSubscriptionLines(cart);

      if (
        vipLines.length !== 1 ||
        Number(vipLines[0]?.quantity || 0) !== 1 ||
        getVipNormalLines(cart).length > 0
      ) {
        await normalizeVip(cart);
      }

      cart = await getCart();
      vipLines = getVipSubscriptionLines(cart);

      return (
        vipLines.length === 1 &&
        Number(vipLines[0].quantity) === 1 &&
        getVipNormalLines(cart).length === 0
      );
    } catch (error) {
      console.error('VIP checkout verification failed:', error);
      return false;
    } finally {
      checkoutRunning = false;
    }
  }

  function hideVipRemoveButton() {
    document
      .querySelectorAll(
        [
          '.kaching-cart-item__remove-btn[aria-label*="VIP Parent Club"]',
          'button[aria-label*="Remove VIP Parent Club"]',
          'button[aria-label*="VIP Parent Club from cart"]'
        ].join(',')
      )
      .forEach((button) => {
        button.style.setProperty('display', 'none', 'important');
        button.style.setProperty('visibility', 'hidden', 'important');
        button.style.setProperty('pointer-events', 'none', 'important');
        button.setAttribute('aria-hidden', 'true');
        button.setAttribute('tabindex', '-1');
        button.disabled = true;
      });
  }

  function createVipGiftCard() {
    const card = document.createElement('div');

    card.id = 'vip-parent-club-progressive-gift';
    card.className =
      'kaching-bundles__progressive-gifts__gift needsclick';

    card.innerHTML = `
      <span class="vip-parent-club-progressive-gift__badge">
        FREE FIRST MONTH
      </span>

      <div class="vip-parent-club-progressive-gift__image">
        ${
          VIP_IMAGE
            ? `<img src="${VIP_IMAGE}" alt="${VIP_TITLE}" loading="lazy">`
            : ''
        }
      </div>

      <p class="vip-parent-club-progressive-gift__title">
        ${VIP_TITLE}
      </p>

      <p class="vip-parent-club-progressive-gift__description">
        Included with every bundle
      </p>

      <span class="vip-parent-club-progressive-gift__status">
        Unlocked
      </span>
    `;

    return card;
  }

  function insertVipGiftCard() {
    const grids = document.querySelectorAll(
      '.kaching-bundles__progressive-gifts__gifts'
    );

    if (!grids.length) {
      return;
    }

    grids.forEach((grid, index) => {
      const existing = grid.querySelector(
        '[data-vip-parent-club-progressive-gift]'
      );

      if (existing) {
        grid.classList.add('has-vip-parent-club-gift');
        return;
      }

      const card = createVipGiftCard();

      card.removeAttribute('id');

      if (index === 0) {
        card.id = 'vip-parent-club-progressive-gift';
      }

      card.setAttribute(
        'data-vip-parent-club-progressive-gift',
        'true'
      );

      const emptyGift = Array.from(grid.children).find((child) => {
        return (
          child.classList.contains(
            'kaching-bundles__progressive-gifts__gift'
          ) &&
          child.textContent.trim() === '' &&
          child.children.length === 0
        );
      });

      if (emptyGift) {
        emptyGift.replaceWith(card);
      } else {
        grid.insertBefore(card, grid.firstElementChild);
      }

      grid.classList.add('has-vip-parent-club-gift');
    });
  }

  function reloadPage() {
    if (reloadStarted) {
      return;
    }

    reloadStarted = true;

    window.setTimeout(() => {
      window.location.reload();
    }, 150);
  }

  function checkLastBundleRemoved() {
    window.clearTimeout(removalTimer);

    let attempts = 0;

    const check = async () => {
      attempts += 1;

      try {
        const cart = await getCart();

        if (getMainQuantity(cart) <= 0) {
          if (getAllVipLines(cart).length) {
            await removeAllVip(cart);
          }

          reloadPage();
          return;
        }

        scheduleSync(50);

        if (attempts < 10) {
          removalTimer = window.setTimeout(check, 120);
        }
      } catch (error) {
        if (attempts < 10) {
          removalTimer = window.setTimeout(check, 150);
        }
      }
    };

    removalTimer = window.setTimeout(check, 60);
  }

  function getRequestUrl(input) {
    if (typeof input === 'string') {
      return input;
    }

    if (
      typeof Request !== 'undefined' &&
      input instanceof Request
    ) {
      return input.url;
    }

    return '';
  }

  function isCartRequest(url = '') {
    return (
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update') ||
      url.includes('/cart/clear')
    );
  }

  window.fetch = async function(input, options = {}) {
    const url = getRequestUrl(input);

    const response = await originalFetch(
      input,
      options
    );

    if (response.ok && isCartRequest(url)) {
      scheduleSync(80);

      window.setTimeout(() => {
        scheduleSync(200);
      }, 200);

      window.setTimeout(() => {
        scheduleSync(500);
      }, 500);
    }

    return response;
  };

  XMLHttpRequest.prototype.open = function(
    method,
    url,
    ...args
  ) {
    this._vipCartRequestUrl = String(url || '');

    return originalXhrOpen.call(
      this,
      method,
      url,
      ...args
    );
  };

  XMLHttpRequest.prototype.send = function(body) {
    const url = this._vipCartRequestUrl || '';

    if (isCartRequest(url)) {
      this.addEventListener(
        'loadend',
        () => {
          if (
            this.status >= 200 &&
            this.status < 400
          ) {
            scheduleSync(100);

            window.setTimeout(() => {
              scheduleSync(300);
            }, 300);
          }
        },
        { once: true }
      );
    }

    return originalXhrSend.call(
      this,
      body
    );
  };

  document.addEventListener(
    'click',
    async (event) => {
      const vipRemoveButton = event.target.closest(
        [
          '.kaching-cart-item__remove-btn[aria-label*="VIP Parent Club"]',
          'button[aria-label*="Remove VIP Parent Club"]',
          'button[aria-label*="VIP Parent Club from cart"]'
        ].join(',')
      );

      if (vipRemoveButton) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      const checkout = event.target.closest(
        [
          'a[href*="/checkout"]',
          'button[name="checkout"]',
          '[data-checkout]',
          '.kaching-cart-checkout-button',
          '[class*="checkout-btn"]',
          '[class*="checkout-button"]'
        ].join(',')
      );

      if (checkout) {
        const href =
          checkout.getAttribute('href') || '';

        const text =
          (checkout.textContent || '')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();

        if (
          href.includes('/checkout') ||
          text.includes('checkout')
        ) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          const ready = await verifyVipBeforeCheckout();

          if (!ready) {
            alert(
              'Your free VIP Parent Club membership is being prepared. Please click checkout again.'
            );
            return;
          }

          window.location.href =
            `${SHOPIFY_ROOT}checkout`;

          return;
        }
      }

      const removeButton = event.target.closest(
        [
          '.kaching-cart-item__remove-btn',
          '[data-cart-remove]',
          '[data-remove-item]',
          '[aria-label*="Remove"]',
          '[aria-label*="remove"]'
        ].join(',')
      );

      if (removeButton) {
        const ariaLabel =
          removeButton.getAttribute('aria-label') || '';

        if (
          !ariaLabel
            .toLowerCase()
            .includes('vip parent club')
        ) {
          checkLastBundleRemoved();
        }
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    async (event) => {
      if (!(event.target instanceof HTMLFormElement)) {
        return;
      }

      const action =
        event.target.getAttribute('action') || '';

      const submitterName =
        event.submitter?.getAttribute('name') || '';

      if (
        action.includes('/checkout') ||
        submitterName === 'checkout'
      ) {
        event.preventDefault();
        event.stopPropagation();

        const ready = await verifyVipBeforeCheckout();

        if (ready) {
          window.location.href =
            `${SHOPIFY_ROOT}checkout`;
        }
      }
    },
    true
  );

  const observer = new MutationObserver(() => {
    window.clearTimeout(mutationDebounce);

    mutationDebounce = window.setTimeout(() => {
      insertVipGiftCard();
      hideVipRemoveButton();
    }, 60);
  });

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

  function startCartMonitor() {
    window.clearInterval(pollTimer);

    pollTimer = window.setInterval(
      async () => {
        if (
          document.visibilityState !== 'visible'
        ) {
          return;
        }

        try {
          const cart = await getCart();

          const mainQuantity =
            getMainQuantity(cart);

          const vipSubscriptionLines =
            getVipSubscriptionLines(cart);

          const normalVipLines =
            getVipNormalLines(cart);

          if (
            mainQuantity > 0 &&
            (
              vipSubscriptionLines.length !== 1 ||
              Number(
                vipSubscriptionLines[0]?.quantity || 0
              ) !== 1 ||
              normalVipLines.length > 0
            )
          ) {
            scheduleSync(20);
          }

          if (
            mainQuantity <= 0 &&
            getAllVipLines(cart).length > 0
          ) {
            scheduleSync(20);
          }
        } catch (error) {}
      },
      1000
    );
  }

  insertVipGiftCard();
  hideVipRemoveButton();
  startCartMonitor();

  window.setTimeout(
    insertVipGiftCard,
    300
  );

  window.setTimeout(
    insertVipGiftCard,
    800
  );

  window.setTimeout(
    insertVipGiftCard,
    1500
  );

  window.setTimeout(
    syncVipState,
    500
  );

  window.addEventListener(
    'focus',
    () => {
      scheduleSync(50);
    }
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      if (
        document.visibilityState === 'visible'
      ) {
        scheduleSync(50);
      }
    }
  );
})();